import {CabObj, isCabObject, isDate, isObject, JSONparse, JSONstringify, newByClassname, uuidOf} from './index.js';
import {plainToInstance} from 'class-transformer';

/**
 * Marker to identify array index references in the serialized data
 */
const POOL_INDEX            = '$dedup-pool-index';
const DEDUP_VERSION         = '$dedup-version';
const DEDUP_CURRENT_VERSION = 1;

/**
 * Serialization result with deduplicated objects
 */
export interface DeduplicatedJSON {
    [DEDUP_VERSION]: number;
    /** Pool of unique objects as an array */
    pool: any[];
    /** Main data with object references replaced by array indices */
    data: any;
}

/**
 * Interface for array index reference objects
 */
interface CabObjRef {
    [POOL_INDEX]: number;
}

/**
 * Convenience function to serialize with deduplication
 */
export function serializeWithDeduplication(data: any): string {
    return new DeduplicatedSerializer().serialize(data);
}

/**
 * Convenience function to deserialize with deduplication
 */
export function deserializeWithDeduplication(jsonString: string): any {
    return new DeduplicatedDeserializer().deserialize(jsonString);
}

/**
 * Serializes objects with deduplication to avoid duplicate objects in JSON
 */
export class DeduplicatedSerializer {
    private readonly poolMap: Map<string, number> = new Map(); // UUID -> array index mapping
    private readonly poolArray: any[]             = []; // Array of serialized objects

    /**
     * Serialize an object or array with deduplication
     */
    public serialize(data: any): string {
        this.poolMap.clear();
        this.poolArray.length = 0;

        const processedData = this.serializeAny(data);

        const result: DeduplicatedJSON = {
            [DEDUP_VERSION]: DEDUP_CURRENT_VERSION,
            pool           : this.poolArray,
            data           : processedData,
        };

        return JSONstringify(result);
    }

    /**
     * Process data for serialization, replacing CabObj instances with references
     */
    private serializeAny(data: any): any {
        if (data !== null && data !== undefined) {
            if (isCabObject(data)) {
                return this.serializeCabObj(data);
            }
            if (Array.isArray(data)) {
                return this.serializeArray(data);
            }
            if (isObject(data)) {
                return this.serializeObject(data);
            }
            const type = typeof data;
            if (type !== 'string' && type !== 'number' && type !== 'boolean' && !isDate(data)) {
                throw new Error(`NOT SERIALIZABLE: ${type}:${Object.prototype.toString.call(data)}`);
            }
        }
        return data;
    }

    private serializeCabObj(obj: CabObj): CabObjRef {
        const uuid: string            = uuidOf(obj);
        let index: number | undefined = this.poolMap.get(uuid)!;
        if (index == undefined) {
            index = this.addToPool(uuid, this.serializeObject(obj));
        }
        return {
            [POOL_INDEX]: index,
        };
    }

    private serializeArray(data: any[]): any {
        return data.map(item => this.serializeAny(item));
    }

    private serializeObject(data: any): any {
        const result: any = {};
        for (const [key, value] of Object.entries(data)) {
            result[key] = this.serializeAny(value);
        }
        return result;
    }

    private addToPool(uuid: string, obj: any): number {
        const index: number = this.poolArray.length;
        this.poolArray.push(obj);
        this.poolMap.set(uuid, index);
        return index;
    }
}

/**
 * Deserializes objects with deduplication support
 */
export class DeduplicatedDeserializer {
    private readonly pool: CabObj[] = [];

    /**
     * Deserialize a deduplicated JSON string
     */
    public deserialize(jsonString: string): any {
        const raw: any = JSONparse(jsonString);
        if (!raw || typeof raw !== 'object' || !(DEDUP_VERSION in raw)) {
            return raw;
        } else {
            return this.deduplicate(raw);
        }
    }

    private deduplicate(parsed: DeduplicatedJSON): any {
        this.pool.length = 0;
        // First pass: create all objects and add to pool
        for (let i = 0; i < parsed.pool.length; i++) {
            this.pool[i] = this.createObjectFromData(parsed.pool[i]);
        }
        // Second pass: resolve all references
        for (let i = 0; i < parsed.pool.length; i++) {
            this.resolveReferences(this.pool[i], parsed.pool[i]);
        }
        // Process the main data
        return this.deserializeAny(parsed.data);
    }

    /**
     * Create a CabObj instance from serialized data
     */
    private createObjectFromData(objData: any): CabObj {
        // Create the object using the proper class constructor
        const obj = newByClassname(objData.classname, objData.uuid);

        // Copy non-reference properties using plainToInstance for proper transformation
        const plainData: any = {};
        for (const [key, value] of Object.entries(objData)) {
            if (key !== 'uuid' && key !== 'classname' && !this.isCabObjRef(value) && !Array.isArray(value)) {
                plainData[key] = value;
            }
        }

        // Use plainToInstance to properly transform the data
        return plainToInstance(obj.constructor as any, {... obj, ... plainData}) as unknown as CabObj;
    }

    /**
     * Resolve UUID references in an object
     */
    private resolveReferences(obj: any, objData: any): void {
        for (const [key, value] of Object.entries(objData)) {
            if (key !== 'uuid' && key !== 'classname') {
                obj[key] = this.deserializeAny(value);
            }
        }
    }

    /**
     * Process data for deserialization, replacing UUID references with actual objects
     */
    private deserializeAny(data: any): any {
        if (data !== null && data !== undefined) {
            if (this.isCabObjRef(data)) {
                return this.deserializeCabObjRef(data);
            }
            if (Array.isArray(data)) {
                return this.deserializeArray(data);
            }
            if (isObject(data)) {
                return this.deserializeObject(data);
            }
            const type = typeof data;
            if (type !== 'string' && type !== 'number' && type !== 'boolean' && !isDate(data)) {
                throw new Error(`NOT SERIALIZABLE: ${type}:${Object.prototype.toString.call(data)}`);
            }
        }
        return data;
    }

    private deserializeCabObjRef(data: CabObjRef): CabObj {
        const index: number  = data[POOL_INDEX];
        const cabObj: CabObj = this.pool[index];
        if (!cabObj) {
            throw new Error(`Referenced object with index ${index} not found in pool`);
        }
        return cabObj;
    }

    private deserializeArray(data: any[]): any {
        return data.map(item => this.deserializeAny(item));
    }

    private deserializeObject(data: object): any {
        const result: any = {};
        for (const [key, value] of Object.entries(data)) {
            result[key] = this.deserializeAny(value);
        }
        return result;
    }

    private isCabObjRef(obj: any): obj is CabObjRef {
        return obj && typeof obj === 'object' && POOL_INDEX in obj;
    }
}
